import { useCallback, useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Dialog, DialogTitle, DialogContent, IconButton, CircularProgress, Typography } from '@mui/material'
import CloseIcon from '@mui/icons-material/Close'
import { PDFViewer } from '@embedpdf/react-pdf-viewer'
import { mediaApi } from '@/api/mediaApi'
import { getMediaBlob } from '@/db/viewerDb'
import { useAppStore } from '@/store/appStore'

interface PdfViewerModalProps {
  open: boolean
  /** The system filename of the media asset (e.g. "document-guid.pdf") */
  systemFileName: string
  title: string
  onClose: () => void
}

type FetchState = 'idle' | 'loading' | 'ready' | 'error'

/**
 * Fullscreen read-only PDF viewer.
 *
 * The offline media cache is read first. Otherwise, while online, the file is
 * fetched through the app's cookie-authenticated Axios client and held in
 * memory as a Blob object URL.  This avoids the browser making a plain GET
 * request (which would not carry the auth cookie in all iframe scenarios) and
 * ensures the raw URL is never exposed to the user.
 *
 * The object URL is revoked when the dialog closes or the component unmounts to
 * prevent memory leaks.
 *
 * All non-viewing features (annotation, redaction, print, export, tools,
 * selection history) are disabled via `disabledCategories` so users cannot
 * download, print, annotate, or modify the document.
 */
export function PdfViewerModal({ open, systemFileName, title, onClose }: PdfViewerModalProps) {
  const { t } = useTranslation()
  const [blobUrl, setBlobUrl] = useState<string | null>(null)
  const [fetchState, setFetchState] = useState<FetchState>('idle')
  const blobUrlRef = useRef<string | null>(null)
  const historyPushedRef = useRef(false)
  const onCloseRef = useRef(onClose)

  // Keep onClose ref up to date
  useEffect(() => {
    onCloseRef.current = onClose
  }, [onClose])

  // Push a history state when the modal opens so the back button can close it
  useEffect(() => {
    if (!open) {
      historyPushedRef.current = false
      return
    }

    // Only push history state once when modal opens
    if (!historyPushedRef.current) {
      window.history.pushState({ pdfViewer: true }, '')
      historyPushedRef.current = true
    }

    // Listen for back button
    const handlePopState = (e: PopStateEvent) => {
      // Only close if we're navigating away from the PDF viewer state
      if (!e.state?.pdfViewer) {
        onCloseRef.current()
      }
    }

    window.addEventListener('popstate', handlePopState)

    return () => {
      window.removeEventListener('popstate', handlePopState)
    }
  }, [open])

  // Fetch the PDF blob whenever the dialog opens with a new systemFileName
  useEffect(() => {
    if (!open || !systemFileName) return

    let cancelled = false
    setFetchState('loading')
    setBlobUrl(null)

    const load = async (): Promise<Blob> => {
      const cached = await getMediaBlob(systemFileName).catch(() => null)
      if (cached) return cached.blob
      if (useAppStore.getState().isOffline) throw new Error('Not cached for offline use')
      return mediaApi.downloadAsset(systemFileName)
    }

    load()
      .then((blob: Blob) => {
        if (cancelled) return
        const url = URL.createObjectURL(blob)
        blobUrlRef.current = url
        setBlobUrl(url)
        setFetchState('ready')
      })
      .catch(() => {
        if (!cancelled) setFetchState('error')
      })

    return () => {
      cancelled = true
    }
  }, [open, systemFileName])

  // Revoke object URL when dialog closes or component unmounts
  const handleClose = useCallback(() => {
    if (blobUrlRef.current) {
      URL.revokeObjectURL(blobUrlRef.current)
      blobUrlRef.current = null
    }
    setBlobUrl(null)
    setFetchState('idle')

    // Always go back in history when closing via the close button
    // The popstate handler will then call onClose()
    if (window.history.state?.pdfViewer) {
      window.history.back()
    } else {
      // Fallback: if there's no history state (edge case), close directly
      onCloseRef.current()
    }
  }, [])

  // Also revoke on unmount (safety net)
  useEffect(() => {
    return () => {
      if (blobUrlRef.current) {
        URL.revokeObjectURL(blobUrlRef.current)
        blobUrlRef.current = null
      }
    }
  }, [])

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      fullScreen
      aria-labelledby="pdf-viewer-title"
      sx={{ zIndex: (theme) => theme.zIndex.modal + 10 }}
    >
      <DialogTitle
        id="pdf-viewer-title"
        className="pdf-viewer-dialog-title"
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          py: 1,
          px: 2,
          borderBottom: '1px solid',
          borderColor: 'divider',
          minHeight: 52,
        }}
      >
        <span className="pdf-viewer-title-text">{title}</span>
        <IconButton
          aria-label={t('pdfViewer.closeViewer')}
          onClick={handleClose}
          size="small"
          sx={{ ml: 1 }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent className="pdf-viewer-dialog-content" sx={{ p: 0, display: 'flex', flexDirection: 'column' }}>
        {fetchState === 'loading' && (
          <div className="pdf-viewer-loading">
            <CircularProgress size={36} />
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1.5 }}>
              {t('pdfViewer.loadingDocument')}
            </Typography>
          </div>
        )}

        {fetchState === 'error' && (
          <div className="pdf-viewer-error">
            <Typography variant="body1" color="error">
              {t('pdfViewer.loadFailed')}
            </Typography>
          </div>
        )}

        {fetchState === 'ready' && blobUrl && (
          <PDFViewer
            className="pdf-viewer-embed"
            style={{ flex: 1, width: '100%', height: '100%' }}
            config={{
              src: blobUrl,
              theme: { preference: 'system' },
              tabBar: 'never',
              disabledCategories: [
                'annotation',
                'redaction',
                'tools',
                'selection',
                'history',
                'document',
                'form',
                'insert'
              ],
            }}
          />
        )}
      </DialogContent>
    </Dialog>
  )
}
